import Vue from 'vue'
import VueRouter from 'vue-router'
// eslint-disable-next-line
import {
  isUserLoggedIn,
  getUserData,
  getHomeRouteForLoggedInUser,
} from '@/auth/utils'
// eslint-disable-next-line
import store from '@/store'
// import { canNavigate } from '@/libs/acl/routeProtection'
import projectAbility from '@/libs/acl/projectAbility'

import auth from './routes/auth'
import billing from './routes/billing'
import referrals from './routes/referrals'
import categories from './routes/categories'
import supplierTags from './routes/supplierTags'
import productTags from './routes/productTags'
import projects from './routes/projects'
import quotes from './routes/quotes'
import payments from './routes/payments'
import shippings from './routes/shippings'
import suppliers from './routes/suppliers'
import branches from './routes/branches'
import products from './routes/products'
import customers from './routes/customers'
import users from './routes/users'
import settings from './routes/settings'
import notifications from './routes/notifications'
import memberships from './routes/memberships'
import purchaseOrders from './routes/purchaseOrders'
import overduePayments from './routes/overduePayments'
import reports from './routes/reports'
import invoices from './routes/invoices'
import wallet from './routes/wallet'
import finance from './routes/finance'
import warehouse from './routes/warehouse'
import suppliesExplosion from './routes/suppliesExplosion'
import tickets from './routes/tickets'
import legal from './routes/legal'
import reloadOnStaleBuild from '@/utils/staleBuildReload'

Vue.use(VueRouter)

const ACCOUNT_REQUIREMENT_EXEMPT_ROUTES = ['complete-account', 'legal-terms', 'legal-privacy', 'confirm-email']

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    { path: '/', redirect: { name: 'suppliers' } },
    ...auth,
    ...categories,
    ...supplierTags,
    ...productTags,
    ...projects,
    ...quotes,
    ...payments,
    ...shippings,
    // finance va antes que suppliers a proposito: /proveedor/:id es un patron de un solo
    // segmento y se traga /proveedor/cuentas-por-cobrar en una recarga, donde el match es por
    // path y no por nombre. Vue Router resuelve por orden de declaracion, asi que la ruta
    // estatica tiene que ir primero.
    ...finance,
    ...suppliers,
    ...branches,
    ...products,
    ...customers,
    ...users,
    ...settings,
    ...notifications,
    ...memberships,
    ...purchaseOrders,
    ...reports,
    ...invoices,
    ...billing,
    ...referrals,
    ...wallet,
    ...overduePayments,
    ...warehouse,
    ...suppliesExplosion,
    ...tickets,
    ...legal,
  ],
})

router.beforeEach(async (to, from, next) => {
  const userData = getUserData()
  const isLoggedIn = isUserLoggedIn()

  if (to.name === 'purchaseOrder') {
    const shouldRedirectToCheckout = !isLoggedIn || userData?.role_name === 'customer'

    if (shouldRedirectToCheckout) {
      return next({
        name: 'checkout',
        params: { quoteId: to.params.quoteId },
        query: to.query,
        hash: to.hash,
      })
    }
  }

  // If route requires auth and user is not logged in
  if (to.meta.requiresAuth && !isLoggedIn) {
    localStorage.setItem('redirectAfterLogin', to.fullPath)
    return next({ name: 'auth-login' })
  }

  if (isLoggedIn) {
    store.commit('auth/setCurrentUser', userData)
  }

  if (isLoggedIn && userData?.role_name === 'customer') {
    if (!store.state.legal.requirementsChecked) {
      await store.dispatch('legal/refreshAccountRequirements')
        .catch(() => store.commit('legal/SET_REQUIREMENTS_CHECKED', true))
    }
    const pendingRequirements = getUserData()?.account_requirements || []
    if (pendingRequirements.length && !ACCOUNT_REQUIREMENT_EXEMPT_ROUTES.includes(to.name)) {
      return next({ name: 'complete-account' })
    }
  }

  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    return next(getHomeRouteForLoggedInUser(userData || null))
  }

  // Admin-only routes (e.g. Tickets)
  if (to.meta.requiresAdmin && userData?.role_name !== 'admin') {
    return next(getHomeRouteForLoggedInUser(userData || null))
  }

  // Pantallas del portal del proveedor. Sin esto, un admin que abre la url ve el listado
  // sin acotar: el backend no filtra a los admins, y ese listado cruza a todos los
  // proveedores.
  if (to.meta.requiresSupplierAdmin && userData?.role_name !== 'supplier_admin') {
    return next(getHomeRouteForLoggedInUser(userData || null))
  }

  // La seccion que publica la pantalla no sobrevive a un cambio de ruta: la siguiente la
  // vuelve a decir si la necesita.
  store.commit('app/SET_ACTIVE_NAV_LINK', null)
  if (isLoggedIn && to.params.quoteId) {
    if (to.meta.needsQuote) {
      store.dispatch('quotes/ensureQuote', to.params.quoteId)
    } else {
      store.dispatch('quotes/markSectionForRoute', to.params.quoteId)
    }
  }

  // El menu lateral y el encabezado se arman con el proyecto del store. En las pantallas
  // que traen el id en la url se carga aqui, para que entrar por liga directa o recargar
  // se vea igual que llegar navegando.
  // La pantalla de cotizaciones decide con la direccion de la obra si esta en zona de
  // cobertura, asi que ahi hay que esperar a tenerla: al entrar por liga directa o recargar,
  // el store todavia trae la obra de la visita anterior y el cliente salia expulsado de una
  // obra que si tiene cobertura.
  let project = null
  if (isLoggedIn && to.params.projectId) {
    const pending = store.dispatch('projects/ensureProject', to.params.projectId).catch(() => null)
    if (to.name === 'quotes') project = await pending
  }

  if (to.name === 'quotes') {
    const resolved = project || store.getters['projects/project']
    if (projectAbility(resolved).can('navigate', 'quotes') === false) {
      return next(resolved?.customer_id
        ? { name: 'projects', params: { customerId: resolved.customer_id } }
        : getHomeRouteForLoggedInUser(userData || null))
    }
  }

  return next()
})

router.afterEach(() => {
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

reloadOnStaleBuild(router)

export default router
